import { useEffect, useState } from "react";
import LieuFormFields from "../../lieux/LieuFormFields";
import Button from "../../../components/Button";
import { useLieuDetail } from "../../../../hooks/useLieuDetail";
import { useUpdateLieu } from "../../../../hooks/useUpdateLieu";
import type { LieuDto } from "../../../../api/lieuxApi";

interface Props {
  lieuId: string;
  onClose: () => void;
}

/**
 * Compact edit form embedded in ParcoursLieuxTab so a lieu can be edited without
 * leaving the parcours page. Loads the full lieu (address + artist links) on mount.
 */
export default function InlineLieuEditor({ lieuId, onClose }: Props) {
  const detail = useLieuDetail(lieuId);
  const update = useUpdateLieu(lieuId);
  const [draft, setDraft] = useState<LieuDto | null>(null);

  useEffect(() => {
    if (!detail.data) return;
    const l = detail.data;
    setDraft({
      name: l.name,
      description: l.description ?? "",
      addressName: l.addressName ?? "",
      address: {
        postcode: l.address?.postcode ?? "",
        city: l.address?.city ?? "",
        street: l.address?.street ?? "",
        latitude: l.address?.latitude,
        longitude: l.address?.longitude,
      },
      artistIds: (l.artists ?? []).map((a) => a.id),
    });
  }, [detail.data]);

  if (detail.isLoading || !draft) {
    return <div className="bo-loading" style={{ padding: "var(--sp-3)" }}>Chargement…</div>;
  }
  if (detail.isError) {
    return <p className="bo-error-text">Impossible de charger ce lieu.</p>;
  }

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate(draft, { onSuccess: () => onClose() });
  };

  return (
    <form onSubmit={onSave} style={{ paddingTop: "var(--sp-4)", borderTop: "1px solid var(--line-soft)" }}>
      <LieuFormFields value={draft} onChange={setDraft} attachedArtists={detail.data.artists ?? []} />
      <div style={{ marginTop: "var(--sp-4)", display: "flex", gap: "var(--sp-2)", justifyContent: "flex-end" }}>
        <Button type="button" variant="ghost" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" variant="primary" loading={update.isPending}>
          Enregistrer
        </Button>
      </div>
      {update.isError && (
        <p className="bo-error-text" style={{ marginTop: "var(--sp-2)" }}>
          Erreur : {(update.error as Error)?.message}
        </p>
      )}
    </form>
  );
}
